/**
 * 模型参数（ModelSettings）的本地持久化。
 *
 * 存储介质：localStorage（键 `zxtalk.modelSettings`）。
 * 读取时做一次归一化，保证外部写入的脏数据不会破坏下游调用。
 */

import type { ModelSettings } from './types';

export const SETTINGS_STORAGE_KEY = 'zxtalk.modelSettings';
export const MODEL_SETTINGS_CHANGED_EVENT = 'zxtalk:model-settings-changed';

/** 需求约定的默认值。 */
export const DEFAULT_MODEL_SETTINGS: ModelSettings = {
  maxContext: 8192,
  thinkingLevel: 'off',
  temperature: 0.8,
  topP: 0.95,
  maxOutputTokens: 1024,
  stream: true,
};

const THINKING_LEVELS: readonly ModelSettings['thinkingLevel'][] = ['off', 'low', 'medium', 'high'];

export function getDefaultModelSettings(): ModelSettings {
  return { ...DEFAULT_MODEL_SETTINGS };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function toPositiveInt(value: unknown, fallback: number): number {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  return Math.floor(num);
}

/** 归一化任意输入为合法的 ModelSettings，缺失/非法字段回落到默认值。 */
export function normalizeModelSettings(input: unknown): ModelSettings {
  const raw = (input ?? {}) as Partial<ModelSettings>;
  const base = getDefaultModelSettings();

  const normalized: ModelSettings = {
    maxContext: toPositiveInt(raw.maxContext, base.maxContext),
    thinkingLevel: THINKING_LEVELS.includes(raw.thinkingLevel!)
      ? (raw.thinkingLevel!)
      : base.thinkingLevel,
    temperature: clamp(typeof raw.temperature === 'number' ? raw.temperature : base.temperature, 0, 2),
    topP: clamp(typeof raw.topP === 'number' ? raw.topP : base.topP, 0, 1),
    maxOutputTokens: toPositiveInt(raw.maxOutputTokens, base.maxOutputTokens),
    stream: typeof raw.stream === 'boolean' ? raw.stream : base.stream,
  };

  if (typeof raw.systemPrompt === 'string') {
    normalized.systemPrompt = raw.systemPrompt;
  }

  return normalized;
}

function storage(): Storage | undefined {
  try {
    return typeof globalThis.localStorage === 'undefined' ? undefined : globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/** 读取设置；未写入过或数据损坏时返回默认值。 */
export function loadModelSettings(): ModelSettings {
  const store = storage();
  if (!store) return getDefaultModelSettings();

  const raw = store.getItem(SETTINGS_STORAGE_KEY);
  if (raw === null) return getDefaultModelSettings();

  try {
    return normalizeModelSettings(JSON.parse(raw));
  } catch {
    return getDefaultModelSettings();
  }
}

/** 覆盖写入设置，返回归一化后的实际值。 */
export function saveModelSettings(settings: ModelSettings): ModelSettings {
  const normalized = normalizeModelSettings(settings);
  storage()?.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(normalized));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MODEL_SETTINGS_CHANGED_EVENT, { detail: normalized }));
  }
  return normalized;
}

/** 局部更新设置（与当前值合并）。 */
export function updateModelSettings(patch: Partial<ModelSettings>): ModelSettings {
  return saveModelSettings({ ...loadModelSettings(), ...patch });
}

/** 恢复默认设置。 */
export function resetModelSettings(): ModelSettings {
  return saveModelSettings(getDefaultModelSettings());
}
